import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useWatch } from "react-hook-form";
import { Grid, Box } from "@mui/material";

import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../components/Form/controls/FormTextField.jsx";
import FormSelect from "../../../components/Form/controls/FormSelect.jsx";
import FormAutocomplete from "../../../components/Form/controls/FormAutocomplete.jsx";
import FormBlankField from "../../../components/Form/controls/FormBlankField.jsx";
import FormInfoField from "../../../components/Form/controls/FormInfoField.jsx";
import FormDetailedRadioGroup from "../../../components/Form/controls/FormDetailedRadioGroup.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";

import {
  userValidationSchema,
  userDefaultValues,
  userOptions,
  optionsApiEndpoints,
  isFieldDisabledByRole,
  getRoleOptions,
} from "./AdminUserFormDefinitions.js";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";

/**
 * Form component for creating or editing a user in the admin section.
 * Uses React Hook Form with Yup validation.
 *
 * @param {object} props - Component props
 * @param {object} props.initialData - Initial data for editing an existing user
 * @param {boolean} props.isEditMode - Whether the form is in edit mode
 * @param {function} props.onSubmit - Function to call when the form is submitted
 * @param {function} props.onCancel - Function to call when the form is cancelled
 * @returns {JSX.Element}
 */
const AdminUserForm = ({
  initialData = {},
  isEditMode = false,
  onSubmit,
  onCancel,
}) => {
  const [kundeOptions, setKundeOptions] = useState([]);
  const { user: actingUser } = useUser();
  const is_customer_admin =
    actingUser?.role_names?.includes("kd_verwaltung") &&
    !actingUser?.role_names?.includes("sysadmin");

  // Merge initial data with default values.
  // Customer admins can only manage users within their own company,
  // so a new user is always pre-assigned to it.
  const defaultValues = {
    ...userDefaultValues,
    ...(is_customer_admin && !isEditMode
      ? { kunde_id: actingUser?.kunde_id }
      : {}),
    ...initialData,
  };

  // Load kunde options on component mount
  useEffect(() => {
    const loadKundeOptions = async () => {
      await backendGet(optionsApiEndpoints.kundeOptions, {
        setResult: setKundeOptions,
      });
    };

    loadKundeOptions();
  }, []);

  const handleSubmit = async (data, formMethods) => {
    await onSubmit(data, formMethods);
  };

  // Role-dependent field rendering
  const RoleDependentFields = () => {
    // Watch the rolle field to conditionally render other fields
    const rolle = useWatch({ name: "rolle" });

    return (
      <>
        {/* Kunde field - conditionally disabled based on role */}
        <Grid item size={{ xs: 12 }}>
          <FormAutocomplete
            name="kunde_id"
            label="Kunde"
            options={kundeOptions}
            disabled={
              isFieldDisabledByRole("kunde_id", rolle) || is_customer_admin
            }
            clearWhenDisabled={!is_customer_admin}
          />
        </Grid>
      </>
    );
  };

  return (
    <Form
      defaultValues={defaultValues}
      resolver={yupResolver(userValidationSchema, { context: { isEditMode } })}
      onSubmit={handleSubmit}
      context={{ isEditMode }}
      mode="onChange"
    >
      <Grid container spacing={2}>
        {/* Role and Status */}
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="rolle"
            label="Rolle"
            options={getRoleOptions(is_customer_admin)}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="status"
            label="Status"
            options={userOptions.status}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormBlankField />
        </Grid>

        {/* Role-dependent fields */}
        <RoleDependentFields />

        {/* Contact Information */}
        <Grid item size={{ xs: 12 }}>
          <FormTextField name="email" label="E-Mail Adresse" fullWidth />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="telefon" label="Telefon" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="fax" label="Telefax" fullWidth />
        </Grid>

        {/* Personal Information */}
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="anrede"
            label="Anrede"
            options={userOptions.anrede}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormTextField name="titel" label="Titel" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormBlankField />
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="vorname" label="Vorname" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FormTextField name="nachname" label="Nachname" fullWidth />
        </Grid>

        {/* Address */}
        <Grid item size={{ xs: 12 }}>
          <FormTextField name="strasse" label="Straße" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 4 }}>
          <FormTextField name="plz" label="PLZ" fullWidth />
        </Grid>
        <Grid item size={{ xs: 12, md: 8 }}>
          <FormTextField name="ort" label="Ort" fullWidth />
        </Grid>

        {/* Portal Configuration */}
        <Grid item size={{ xs: 12 }}>
          <FormInfoField label="Konfiguration des Schadenportals" />
        </Grid>

        <Grid item size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="rpp"
            label="Zeilen pro Seite"
            options={userOptions.rpp}
          />
        </Grid>

        <Grid item size={{ xs: 12 }}>
          <FormDetailedRadioGroup
            name="level_benachrichtigung"
            label="Wie möchten Sie über Aktualisierungen im Schadenportal informiert werden?"
            options={userOptions.level_benachrichtigung}
          />
        </Grid>

        {/* Form Actions */}
        <Grid item size={{ xs: 12 }}>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            {onCancel && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Abbrechen
              </button>
            )}
            <SubmitButton>
              {isEditMode ? "Speichern" : "Erstellen"}
            </SubmitButton>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};

export default AdminUserForm;
